/**
 * Public @username profile page — the root-level identity surface.
 *
 * Layout: hero (avatar + name + @username) → CTAs (Circle / Send gratitude /
 * View wall) → preview of 3 public gratitude cards if wall is public.
 * Includes a RequestCircleDialog with mandatory role picker + optional note.
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import SEO from "../components/SEO";
import { googleSignIn } from "../lib/googleSignIn";
import GratitudeCard from "../components/GratitudeCard";
import ValueIcon from "../components/ValueIcon";
import PublicAddGratitudeDialog from "../components/PublicAddGratitudeDialog";
import AddGratitudeDialog from "../components/AddGratitudeDialog";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { UserPlus, HeartHandshake, Check, ArrowRight, Clock, Heart, Lock, UserCheck, Lightbulb, ChevronDown, Users, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const API = "/api";

// localStorage key used to queue an anonymous visitor's intent so it can
// be re-executed after they complete OAuth and return to the page.
const PENDING_INTENT_KEY = "evoluras_pending_profile_intent";

const setPendingIntent = (intent) => {
  try {
    localStorage.setItem(PENDING_INTENT_KEY, JSON.stringify({
      ...intent,
      savedAt: Date.now(),
    }));
  } catch { /* quota / privacy-mode — silently skip */ }
};

const consumePendingIntent = (username) => {
  try {
    const raw = localStorage.getItem(PENDING_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Drop intents older than 30 minutes, or for a different user.
    if (!parsed || parsed.username !== username) return null;
    if (Date.now() - (parsed.savedAt || 0) > 30 * 60 * 1000) {
      localStorage.removeItem(PENDING_INTENT_KEY);
      return null;
    }
    localStorage.removeItem(PENDING_INTENT_KEY);
    return parsed;
  } catch { return null; }
};

// Canonical relationship list — kept in sync with NetworkPage's invite Select
// (lines 539–550). The backend's ALLOWED_RELATIONSHIPS allowlist mirrors this.
const RELATIONSHIPS = [
  { key: "friend", label: "Friend" },
  { key: "family", label: "Family" },
  { key: "coworker", label: "Coworker" },
  { key: "mentor", label: "Mentor" },
  { key: "mentee", label: "Mentee" },
  { key: "professor", label: "Professor" },
  { key: "student", label: "Student" },
  { key: "coach", label: "Coach" },
  { key: "coachee", label: "Coachee" },
  { key: "business_partner", label: "Business Partner" },
  { key: "therapist", label: "Therapist" },
  { key: "other", label: "Other" },
];

// ── Circle connection request modal ───────────────────────────
const RequestCircleDialog = ({ open, onOpenChange, ownerFirstName, username, onSuccess }) => {
  const [role, setRole] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) { setRole(""); setMessage(""); } }, [open]);

  const submit = async () => {
    if (!role) return toast.error("Pick how you know them");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/profile/${encodeURIComponent(username)}/connect`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role, message: message.trim() || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Couldn't send request");
      toast.success(data.state === "connected" ? `Already in ${ownerFirstName}'s Circle` : `Request sent to ${ownerFirstName}`);
      onOpenChange(false);
      onSuccess?.(data.state);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="request-circle-dialog">
        <div className="flex items-center gap-2.5 mb-4">
          <UserPlus className="w-4 h-4 text-violet-600" />
          <h2 className="text-base font-semibold text-slate-900">
            Connect with {ownerFirstName}
          </h2>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">
              How do you know them?
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {RELATIONSHIPS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRole(r.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    role === r.key
                      ? "bg-violet-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`request-role-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="req-msg" className="text-xs font-medium text-slate-600">
              A quick note <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Textarea id="req-msg" value={message} onChange={(e) => setMessage(e.target.value)}
              rows={3} maxLength={300} placeholder={`Hi ${ownerFirstName}, we met at…`}
              className="resize-none text-sm"
              data-testid="request-message" />
            <div className="text-[10px] text-slate-400 text-right mt-0.5">{message.length}/300</div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="request-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || !role}
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
              data-testid="request-send"
            >
              {submitting ? "Sending…" : "Send request"}
              {!submitting && <ArrowRight className="w-3.5 h-3.5 ml-1.5" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


/**
 * ShareInsightDialog — proactive SWOT insight composer for Circle members.
 *
 * Multi-step flow that walks the user through each of the four SWOT
 * quadrants (Strengths → Weaknesses → Opportunities → Threats), letting
 * them add ANY number of items per quadrant, plus an optional final
 * comment. All collected items are POSTed to `/api/insights/proactive`
 * in a single call (the endpoint already accepts the full
 * `suggested_swot_items` shape with all 4 quadrants).
 *
 * The recipient sees the result identically to a reactive insight —
 * no client-side branching needed downstream.
 */
const QUADRANTS = [
  {
    key: "strengths",
    label: "Strengths",
    title: (name) => `${name}'s strengths`,
    subtitle: (name) => `Things ${name} does well — qualities, talents, or behaviours you admire.`,
    placeholder: (name) => `E.g. ${name} stays calm under pressure...`,
    accent: "#a78bfa",
  },
  {
    key: "weaknesses",
    label: "Weaknesses",
    title: (name) => `${name}'s blind spots`,
    subtitle: (name) => `Areas where ${name} could grow — patterns or habits worth being aware of.`,
    placeholder: (name) => `E.g. ${name} sometimes overcommits...`,
    accent: "#f87171",
  },
  {
    key: "opportunities",
    label: "Opportunities",
    title: (name) => `${name}'s opportunities`,
    subtitle: (name) => `Doors that seem open to ${name} right now — directions, paths, possibilities.`,
    placeholder: (name) => `E.g. They could lean into mentoring...`,
    accent: "#34d399",
  },
  {
    key: "threats",
    label: "Threats",
    title: (name) => `${name}'s threats`,
    subtitle: (name) => `External pressures or patterns that could hold ${name} back.`,
    placeholder: (name) => `E.g. The pace of their team's growth...`,
    accent: "#fbbf24",
  },
];

const ShareInsightDialog = ({ open, onOpenChange, ownerName, ownerFirstName, connectionId }) => {
  // step 0..3 = each SWOT quadrant; step 4 = optional comment + submit
  const [step, setStep] = useState(0);
  const [suggestions, setSuggestions] = useState({
    strengths: [], weaknesses: [], opportunities: [], threats: [],
  });
  const [drafts, setDrafts] = useState({
    strengths: "", weaknesses: "", opportunities: "", threats: "",
  });
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset on close so each open is a fresh page.
  useEffect(() => {
    if (!open) {
      setStep(0);
      setSuggestions({ strengths: [], weaknesses: [], opportunities: [], threats: [] });
      setDrafts({ strengths: "", weaknesses: "", opportunities: "", threats: "" });
      setComment("");
    }
  }, [open]);

  const firstName = ownerFirstName || ownerName || "them";
  const currentQ = QUADRANTS[step] || null;

  // Commit the current draft into the suggestions array. Empty drafts
  // are silently ignored so users can advance without typing anything.
  const commitDraft = (key) => {
    const value = (drafts[key] || "").trim();
    if (!value) return;
    setSuggestions((prev) => ({ ...prev, [key]: [...prev[key], value] }));
    setDrafts((prev) => ({ ...prev, [key]: "" }));
  };

  const removeItem = (key, idx) => {
    setSuggestions((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== idx),
    }));
  };

  const totalItems = Object.values(suggestions).reduce((sum, arr) => sum + arr.length, 0);

  const goNext = () => {
    // Auto-commit any pending draft on the current quadrant before advancing.
    if (currentQ) commitDraft(currentQ.key);
    setStep((s) => Math.min(4, s + 1));
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    // Auto-commit comment-step has no draft; final flush of any leftover SWOT draft.
    if (totalItems === 0) {
      // If they truly added nothing, also flush current draft just in case.
      QUADRANTS.forEach((q) => commitDraft(q.key));
    }
    const finalSuggestions = {
      strengths: [...suggestions.strengths],
      weaknesses: [...suggestions.weaknesses],
      opportunities: [...suggestions.opportunities],
      threats: [...suggestions.threats],
      unsure: [],
    };
    // Late commit: if user clicked Submit without leaving a step, fold any
    // current draft into the bundle.
    QUADRANTS.forEach((q) => {
      const v = (drafts[q.key] || "").trim();
      if (v && !finalSuggestions[q.key].includes(v)) finalSuggestions[q.key].push(v);
    });
    const total = Object.values(finalSuggestions).reduce((s, a) => s + (Array.isArray(a) ? a.length : 0), 0);
    if (total === 0) {
      toast.error("Add at least one observation before sharing.");
      return;
    }
    if (!connectionId) {
      toast.error("Missing connection. Refresh and try again.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/insights/proactive`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          connection_id: connectionId,
          tool_type: "swot",
          insight_response: comment.trim() || "(SWOT suggestions provided)",
          comment: comment.trim() || null,
          suggested_swot_items: finalSuggestions,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail?.message || err?.detail || "Failed to share");
      }
      toast.success(`Insight shared with ${firstName}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(typeof e?.message === "string" ? e.message : "Couldn't share. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isCommentStep = step === 4;
  const stepIndexHuman = step + 1;
  const totalSteps = 5;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="share-insight-dialog">
        {/* Header */}
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: "rgba(167,139,250,0.18)" }}>
            <Sparkles className="w-4 h-4" style={{ color: "var(--color-brand-primary)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
              Share an insight with {firstName}
            </h2>
            <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
              Step {stepIndexHuman} of {totalSteps}
            </p>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-5" data-testid="shareins-progress">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <span
              key={i}
              className="h-1 flex-1 rounded-full transition-colors"
              style={{
                background: i <= step
                  ? (i < 4 ? QUADRANTS[i]?.accent : "var(--color-brand-primary)")
                  : "rgba(255,255,255,0.08)",
              }}
            />
          ))}
        </div>

        {/* Body */}
        {!isCommentStep && currentQ ? (
          <div className="space-y-4" data-testid={`shareins-step-${currentQ.key}`}>
            <div>
              <h3 className="text-sm font-semibold mb-1" style={{ color: "var(--color-text-primary)" }}>
                {currentQ.title(firstName)}
              </h3>
              <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
                {currentQ.subtitle(firstName)}
              </p>
            </div>

            {/* Already-added items */}
            {suggestions[currentQ.key].length > 0 && (
              <ul className="space-y-1.5">
                {suggestions[currentQ.key].map((item, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 pl-3 pr-2 py-2 rounded-lg"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      borderLeft: `3px solid ${currentQ.accent}`,
                    }}
                    data-testid={`shareins-item-${currentQ.key}-${idx}`}
                  >
                    <p className="text-sm flex-1" style={{ color: "var(--color-text-primary)" }}>
                      {item}
                    </p>
                    <button
                      type="button"
                      onClick={() => removeItem(currentQ.key, idx)}
                      aria-label="Remove"
                      className="text-xs opacity-60 hover:opacity-100 transition-opacity"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* New-item input */}
            <div className="flex gap-2">
              <Textarea
                value={drafts[currentQ.key]}
                onChange={(e) => setDrafts((p) => ({ ...p, [currentQ.key]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    commitDraft(currentQ.key);
                  }
                }}
                placeholder={currentQ.placeholder(firstName)}
                maxLength={280}
                className="min-h-[64px] text-sm flex-1"
                data-testid={`shareins-draft-${currentQ.key}`}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => commitDraft(currentQ.key)}
                disabled={!drafts[currentQ.key].trim()}
                className="self-end rounded-full px-4"
                data-testid={`shareins-add-${currentQ.key}`}
              >
                Add
              </Button>
            </div>
            <p className="text-[10px]" style={{ color: "var(--color-text-placeholder)" }}>
              You can add as many as you want. Skip the step if nothing comes to mind.
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="shareins-step-comment">
            {/* Summary */}
            <div>
              <h3 className="text-sm font-semibold mb-2" style={{ color: "var(--color-text-primary)" }}>
                Ready to share with {firstName}
              </h3>
              <ul className="space-y-1.5 text-xs" style={{ color: "var(--color-text-secondary)" }}>
                {QUADRANTS.map((q) => {
                  const n = suggestions[q.key].length + (drafts[q.key].trim() ? 1 : 0);
                  return (
                    <li key={q.key} className="flex items-center gap-2">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: n > 0 ? q.accent : "rgba(255,255,255,0.15)" }}
                      />
                      <span style={{ color: "var(--color-text-primary)" }}>{q.label}</span>
                      <span className="ml-auto tabular-nums">{n}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Optional final note */}
            <div>
              <label className="text-[11px] uppercase tracking-wide font-medium block mb-2"
                style={{ color: "var(--color-text-secondary)" }}>
                Optional — a short note
              </label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={`Anything you want ${firstName} to know about why you're sharing this…`}
                maxLength={500}
                className="min-h-[72px] text-sm"
                data-testid="shareins-comment"
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 pt-4 mt-2 border-t"
          style={{ borderColor: "var(--color-border-default)" }}>
          <button
            type="button"
            onClick={step === 0 ? () => onOpenChange(false) : goBack}
            disabled={submitting}
            className="text-xs font-medium hover:underline"
            style={{ color: "var(--color-text-secondary)" }}
            data-testid="shareins-back"
          >
            {step === 0 ? "Cancel" : "Back"}
          </button>
          {!isCommentStep ? (
            <Button
              onClick={goNext}
              disabled={submitting}
              className="rounded-full"
              style={{
                background: "var(--color-brand-primary)",
                color: "var(--color-text-on-brand)",
              }}
              data-testid="shareins-next"
            >
              {step === 3 ? "Review" : "Next"}
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={submitting}
              className="rounded-full"
              style={{
                background: "var(--color-brand-primary)",
                color: "var(--color-text-on-brand)",
              }}
              data-testid="shareins-submit"
            >
              {submitting ? "Sharing…" : "Share insight"}
              {!submitting && <Sparkles className="w-3.5 h-3.5 ml-1.5" />}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ── Circle-only "Exchange" section ────────────────────────────
// Shows the warmth of an actual relationship: insights this person gave
// you on your tools + insights you gave them, plus gratitudes exchanged
// in both directions. Only rendered when the viewer is connected.
const ToolLabel = ({ tool }) => {
  const map = {
    swot: "Personal SWOT",
    core_values: "Core Values",
    wheel: "Wheel of Life",
  };
  return <span>{map[tool] || tool}</span>;
};

const InsightRow = ({ item, direction, firstName }) => {
  const dateStr = item.created_at
    ? new Date(item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";
  const hasAnySwotSuggestion = item.suggested_swot_items &&
    ["strengths", "weaknesses", "opportunities", "threats", "unsure"].some(
      k => Array.isArray(item.suggested_swot_items[k]) && item.suggested_swot_items[k].length > 0
    );
  const hasUsefulResponse = hasAnySwotSuggestion
    || (item.insight_response && item.insight_response !== "(SWOT suggestions provided)");
  return (
    <div className="rounded-xl bg-white border border-violet-100 p-4"
      data-testid={`exchange-insight-${direction}-${item.request_id}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs">
          <Lightbulb className="w-3.5 h-3.5 text-violet-600" />
          <span className="font-semibold text-slate-900">
            <ToolLabel tool={item.tool_type} />
          </span>
          <span className="text-slate-400">·</span>
          <span className="text-slate-500">
            {direction === "from_them" ? `From ${firstName}` : `To ${firstName}`}
          </span>
        </div>
        <span className="text-[10px] text-slate-400">{dateStr}</span>
      </div>
      {item.message && (
        <p className="text-[11px] italic text-slate-500 mb-2">
          “{item.message}”
        </p>
      )}
      {hasUsefulResponse ? (
        <InsightResponsePreview response={item.insight_response} suggestedSwotItems={item.suggested_swot_items} />
      ) : (
        <p className="text-xs text-slate-400 italic">No written response.</p>
      )}
      {item.comment && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
            {direction === "from_them" ? `${firstName}'s note` : "Your note"}
          </p>
          <p className="text-xs text-slate-600 italic whitespace-pre-wrap">“{item.comment}”</p>
        </div>
      )}
    </div>
  );
};

// The `insight_response` payload varies per tool (SWOT has 4 buckets,
// Core Values has a votes array, Wheel has area scores). We don't duplicate
// the full rendering logic here — we just pick the most readable 1–3 line
// preview for each shape. The "Insights" tool page remains the source of truth.
const InsightResponsePreview = ({ response, suggestedSwotItems }) => {
  // For SWOT, the canonical structured data lives on `suggested_swot_items`,
  // not on `insight_response` (which is typically the string
  // "(SWOT suggestions provided)"). If we have the structured data, render
  // the actual suggestions as colored chips per quadrant.
  if (suggestedSwotItems && typeof suggestedSwotItems === "object") {
    const quadrants = [
      { key: "strengths",    label: "Strengths",    color: "text-emerald-700",  bg: "bg-emerald-50",  dot: "bg-emerald-500" },
      { key: "weaknesses",   label: "Weaknesses",   color: "text-rose-700",     bg: "bg-rose-50",     dot: "bg-rose-500" },
      { key: "opportunities", label: "Opportunities", color: "text-sky-700",    bg: "bg-sky-50",      dot: "bg-sky-500" },
      { key: "threats",      label: "Threats",      color: "text-amber-700",    bg: "bg-amber-50",    dot: "bg-amber-500" },
      { key: "unsure",       label: "Unsure",       color: "text-slate-600",    bg: "bg-slate-50",    dot: "bg-slate-400" },
    ];
    const any = quadrants.some(q => (suggestedSwotItems[q.key] || []).length > 0);
    if (!any) return <p className="text-xs text-slate-400 italic">No items suggested.</p>;
    return (
      <div className="space-y-2">
        {quadrants.map(q => {
          const items = suggestedSwotItems[q.key] || [];
          if (items.length === 0) return null;
          return (
            <div key={q.key}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className={`w-1.5 h-1.5 rounded-full ${q.dot}`} />
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${q.color}`}>
                  {q.label}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {items.map((it, idx) => (
                  <span key={idx}
                    className={`inline-block text-xs px-2.5 py-1 rounded-full ${q.bg} ${q.color}`}
                    data-testid={`exchange-swot-${q.key}-${idx}`}>
                    {it}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (!response || typeof response !== "object") {
    if (typeof response === "string" && response && response !== "(SWOT suggestions provided)") {
      return <p className="text-xs text-slate-600 whitespace-pre-wrap">{response}</p>;
    }
    return null;
  }
  // Core Values vote-shape
  if (Array.isArray(response.votes)) {
    return (
      <p className="text-xs text-slate-600">
        Voted: {response.votes.slice(0, 3).map((v) => v.value || v.label).filter(Boolean).join(", ")}
        {response.votes.length > 3 ? "…" : ""}
      </p>
    );
  }
  if (typeof response.text === "string") {
    return <p className="text-xs text-slate-600 whitespace-pre-wrap">{response.text}</p>;
  }
  if (typeof response.note === "string") {
    return <p className="text-xs text-slate-600 whitespace-pre-wrap">{response.note}</p>;
  }
  return <p className="text-xs text-slate-400 italic">View the full insight on its tool page.</p>;
};

// Visual mapping for agree/disagree/unsure votes.
const VOTE_STYLES = {
  agree:    { label: "Agree",         color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", icon: "✓" },
  disagree: { label: "Disagree",      color: "text-rose-700",    bg: "bg-rose-50",    border: "border-rose-200",    icon: "✗" },
  unsure:   { label: "Not observed",  color: "text-slate-600",   bg: "bg-slate-50",   border: "border-slate-200",   icon: "?" },
};

const VoteRow = ({ vote, direction, firstName }) => {
  const style = VOTE_STYLES[vote.vote] || VOTE_STYLES.unsure;
  const catColor = {
    strengths: "text-emerald-600",
    weaknesses: "text-rose-600",
    opportunities: "text-sky-600",
    threats: "text-amber-600",
  }[vote.category] || "text-slate-600";
  return (
    <div className={`flex items-start gap-2.5 rounded-lg bg-white border border-slate-100 p-3`}
      data-testid={`exchange-vote-${direction}-${vote.item_id}`}>
      <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${style.bg} ${style.color} border ${style.border}`}>
        {style.icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-[10px] mb-0.5 flex-wrap">
          <span className={`font-semibold uppercase tracking-wider ${catColor}`}>
            {vote.category}
          </span>
          <span className="text-slate-300">·</span>
          <span className={`font-medium ${style.color}`}>
            {direction === "from_them" ? `${firstName} ${style.label.toLowerCase()}s` : `You ${style.label.toLowerCase()}d`}
          </span>
        </div>
        <p className="text-sm text-slate-800">{vote.content}</p>
        {vote.comment && (
          <p className="text-xs italic text-slate-500 mt-1">“{vote.comment}”</p>
        )}
      </div>
    </div>
  );
};

const VotesPanel = ({ votes, direction, firstName }) => {
  if (!votes || votes.length === 0) return null;
  return (
    <div className="space-y-1.5" data-testid={`exchange-vote-summary-${direction}`}>
      {votes.map(v => (
        <VoteRow key={`${direction}-${v.item_id}`} vote={v} direction={direction} firstName={firstName} />
      ))}
    </div>
  );
};

// An insight row is worth displaying only when it carries real content —
// actual SWOT suggestion chips, a text response, or a comment from the
// responder. A bare request ("I'd love your insights on my SWOT") with
// "No written response" adds noise without substance, so we drop it.
const insightHasSubstance = (item) => {
  const swot = item.suggested_swot_items;
  const hasSwot = swot && typeof swot === "object" &&
    ["strengths", "weaknesses", "opportunities", "threats", "unsure"].some(
      (k) => Array.isArray(swot[k]) && swot[k].length > 0
    );
  if (hasSwot) return true;
  if (item.comment && String(item.comment).trim()) return true;
  const resp = item.insight_response;
  if (typeof resp === "string" &&
      resp.trim() && resp !== "(SWOT suggestions provided)") return true;
  if (resp && typeof resp === "object") {
    if (Array.isArray(resp.votes) && resp.votes.length > 0) return true;
    if (typeof resp.text === "string" && resp.text.trim()) return true;
    if (typeof resp.note === "string" && resp.note.trim()) return true;
  }
  return false;
};

// ── Collapsible accordion section for the Exchange panel ──────
// Collapsed by default; header shows heading + count + a subtle summary
// of what's inside (e.g., vote tallies). User choses depth.
const ExchangeAccordion = ({ title, count, summary, defaultOpen = false, testId, children, emptyFallback }) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasContent = count > 0;
  return (
    <div
      className="rounded-xl border border-slate-100 overflow-hidden"
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => hasContent && setOpen(v => !v)}
        disabled={!hasContent}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${
          hasContent ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"
        } bg-white`}
        data-testid={`${testId}-toggle`}
      >
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-slate-800">
            {title}
            <span className={`ml-2 text-[10px] font-medium ${hasContent ? "text-violet-600" : "text-slate-400"}`}>
              {count}
            </span>
          </p>
          {summary && hasContent && (
            <p className="text-[10px] text-slate-400 mt-0.5">{summary}</p>
          )}
        </div>
        {hasContent && (
          <ChevronDown
            className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {hasContent && open && (
        <div className="px-4 py-3 bg-slate-50/50" data-testid={`${testId}-body`}>
          {children}
        </div>
      )}
      {!hasContent && emptyFallback && (
        <div className="px-4 pb-3 text-[11px] text-slate-400 italic">
          {emptyFallback}
        </div>
      )}
    </div>
  );
};

const CircleExchangeSection = ({ exchange, firstName }) => {
  const [tab, setTab] = useState("insights"); // "insights" | "gratitudes"

  if (!exchange) {
    return (
      <div className="rounded-2xl bg-white/70 border border-violet-100 p-6 mb-10 text-center"
        data-testid="exchange-loading">
        <div className="w-6 h-6 mx-auto border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const insightsFromThem = (exchange.insights?.from_them || []).filter(insightHasSubstance);
  const insightsToThem = (exchange.insights?.to_them || []).filter(insightHasSubstance);
  const gratsFromThem = exchange.gratitudes?.from_them || [];
  const gratsToThem = exchange.gratitudes?.to_them || [];
  const votesFromThem = exchange.swot_votes?.from_them || [];
  const votesToThem = exchange.swot_votes?.to_them || [];
  const insightsTotal = insightsFromThem.length + insightsToThem.length + votesFromThem.length + votesToThem.length;
  const gratsTotal = gratsFromThem.length + gratsToThem.length;
  // Pre-compute short summaries shown on collapsed accordion headers —
  // makes the section scannable without opening anything.
  const voteSummary = (votes) => {
    if (!votes || votes.length === 0) return "";
    const counts = votes.reduce(
      (acc, v) => { acc[v.vote] = (acc[v.vote] || 0) + 1; return acc; },
      {}
    );
    const parts = [];
    if (counts.agree) parts.push(`✓ ${counts.agree}`);
    if (counts.disagree) parts.push(`✗ ${counts.disagree}`);
    if (counts.unsure) parts.push(`? ${counts.unsure}`);
    return parts.join(" · ");
  };
  const lastInsightDate = (list) => {
    if (!list || list.length === 0) return "";
    const d = list[0].created_at ? new Date(list[0].created_at) : null;
    if (!d || Number.isNaN(d.getTime())) return "";
    return `Latest ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  };
  const lastGratDate = lastInsightDate;

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-2xl bg-white/70 border border-violet-100 p-5 sm:p-6 mb-10"
      data-testid="circle-exchange"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <UserCheck className="w-3.5 h-3.5 text-violet-600" />
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              Your exchange with {firstName}
            </p>
          </div>
          <h2 className="text-lg font-bold text-slate-900">
            What you've shared
          </h2>
        </div>
        <div className="inline-flex rounded-full p-0.5 text-xs font-medium bg-slate-100"
          data-testid="exchange-tab-toggle">
          <button
            onClick={() => setTab("insights")}
            className={`px-3 py-1 rounded-full transition-colors ${
              tab === "insights" ? "bg-violet-600 text-white" : "text-slate-600"
            }`}
            data-testid="exchange-tab-insights"
          >
            Insights · {insightsTotal}
          </button>
          <button
            onClick={() => setTab("gratitudes")}
            className={`px-3 py-1 rounded-full transition-colors ${
              tab === "gratitudes" ? "bg-violet-600 text-white" : "text-slate-600"
            }`}
            data-testid="exchange-tab-gratitudes"
          >
            Gratitudes · {gratsTotal}
          </button>
        </div>
      </div>

      {tab === "insights" && (
        <div className="space-y-2" data-testid="exchange-insights-panel">
          <ExchangeAccordion
            title={`${firstName}'s insights on you`}
            count={insightsFromThem.length}
            summary={lastInsightDate(insightsFromThem)}
            testId="exchange-acc-insights-from"
            emptyFallback={`${firstName} hasn't shared any insights with you yet.`}
          >
            <div className="space-y-2.5">
              {insightsFromThem.map((i) => (
                <InsightRow key={i.request_id} item={i} direction="from_them" firstName={firstName} />
              ))}
            </div>
          </ExchangeAccordion>

          <ExchangeAccordion
            title={`${firstName}'s votes on your SWOT`}
            count={votesFromThem.length}
            summary={voteSummary(votesFromThem)}
            testId="exchange-acc-votes-from"
            emptyFallback={`${firstName} hasn't voted on any of your SWOT items.`}
          >
            <VotesPanel votes={votesFromThem} direction="from_them" firstName={firstName} />
          </ExchangeAccordion>

          <ExchangeAccordion
            title={`Insights you gave ${firstName}`}
            count={insightsToThem.length}
            summary={lastInsightDate(insightsToThem)}
            testId="exchange-acc-insights-to"
            emptyFallback={`You haven't given ${firstName} any insights yet.`}
          >
            <div className="space-y-2.5">
              {insightsToThem.map((i) => (
                <InsightRow key={i.request_id} item={i} direction="to_them" firstName={firstName} />
              ))}
            </div>
          </ExchangeAccordion>

          <ExchangeAccordion
            title={`Your votes on ${firstName}'s SWOT`}
            count={votesToThem.length}
            summary={voteSummary(votesToThem)}
            testId="exchange-acc-votes-to"
            emptyFallback={`You haven't voted on ${firstName}'s SWOT items.`}
          >
            <VotesPanel votes={votesToThem} direction="to_them" firstName={firstName} />
          </ExchangeAccordion>
        </div>
      )}

      {tab === "gratitudes" && (
        <div className="space-y-2" data-testid="exchange-gratitudes-panel">
          <ExchangeAccordion
            title={`Gratitudes from ${firstName}`}
            count={gratsFromThem.length}
            summary={lastGratDate(gratsFromThem)}
            testId="exchange-acc-grats-from"
            emptyFallback={`No gratitudes from ${firstName} yet.`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {gratsFromThem.map((c) => (
                <GratitudeCard key={c.card_id}
                  card={{ ...c, perspective: "received", visibility: c.target_visibility || c.visibility || "circle" }}
                  readOnly />
              ))}
            </div>
          </ExchangeAccordion>

          <ExchangeAccordion
            title={`Gratitudes you sent ${firstName}`}
            count={gratsToThem.length}
            summary={lastGratDate(gratsToThem)}
            testId="exchange-acc-grats-to"
            emptyFallback={`You haven't sent ${firstName} any gratitudes yet.`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {gratsToThem.map((c) => (
                <GratitudeCard key={c.card_id}
                  card={{ ...c, perspective: "given", visibility: c.target_visibility || c.visibility || "circle" }}
                  readOnly />
              ))}
            </div>
          </ExchangeAccordion>
        </div>
      )}
    </motion.section>
  );
};


// ── Main page ─────────────────────────────────────────────────

// Small icon+label secondary action pill used in the hero CTA cluster.
// Keeps shape consistent with the primary pill while staying visually
// lighter. Labels remove the ambiguity of icon-only buttons.
const SecondaryPill = ({ onClick, label, icon, testId, active = false, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-xs font-semibold transition-all disabled:opacity-50"
    style={{
      background: active ? "rgba(167,139,250,0.18)" : "rgba(255,255,255,0.05)",
      border: active
        ? "1px solid rgba(167,139,250,0.5)"
        : "1px solid rgba(255,255,255,0.08)",
      color: active ? "#c4b5fd" : "var(--color-text-primary)",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = active
        ? "rgba(167,139,250,0.28)"
        : "rgba(255,255,255,0.10)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = active
        ? "rgba(167,139,250,0.18)"
        : "rgba(255,255,255,0.05)";
    }}
    data-testid={testId}
  >
    {icon}
    <span>{label}</span>
  </button>
);

// Small circular icon-only action button — kept for rare cases where a
// label would add visual noise (e.g., Send gratitude + Share insight
// which already share context with the Circle membership CTA).
const IconAction = ({ onClick, title, icon, testId, active = false, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    className="w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-50"
    style={{
      background: active ? "rgba(167,139,250,0.18)" : "rgba(255,255,255,0.05)",
      border: active
        ? "1px solid rgba(167,139,250,0.5)"
        : "1px solid rgba(255,255,255,0.08)",
      color: active ? "#c4b5fd" : "var(--color-text-primary)",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = active
        ? "rgba(167,139,250,0.28)"
        : "rgba(255,255,255,0.10)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = active
        ? "rgba(167,139,250,0.18)"
        : "rgba(255,255,255,0.05)";
    }}
    data-testid={testId}
  >
    {icon}
  </button>
);

// Circle ribbon — always-expanded avatar stack + count. Sits on the
// cover bottom-right and signals "this person is real and connected"
// at a glance. (Earlier hover-collapse pattern was removed — the
// stack feels warmer when it's always visible.)
const CircleRibbon = ({ members, count, firstName }) => {
  if (!members || members.length === 0) return null;
  return (
    <div
      className="hidden sm:flex absolute bottom-4 right-4 items-center gap-2.5 rounded-full pl-2 pr-3.5 py-1.5"
      style={{
        background: "rgba(20, 10, 50, 0.55)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.14)",
        boxShadow: "0 4px 20px rgba(20, 10, 50, 0.35)",
      }}
      data-testid="cover-circle-ribbon"
    >
      <div className="flex -space-x-1.5">
        {members.slice(0, 5).map((m, i) => (
          <div
            key={m.username || i}
            className="w-6 h-6 rounded-full bg-violet-800 text-violet-100 text-[10px] font-semibold flex items-center justify-center overflow-hidden"
            style={{ zIndex: 10 - i, boxShadow: "0 0 0 2px rgba(20,10,50,0.9)" }}
            title={m.first_name}
          >
            {m.picture ? (
              <img src={m.picture} alt={m.first_name} className="w-full h-full object-cover" />
            ) : (
              <span>{(m.first_name || "?").charAt(0)}</span>
            )}
          </div>
        ))}
      </div>
      <span className="text-xs font-semibold text-white whitespace-nowrap">
        {count ?? members.length} in {firstName}'s Circle
      </span>
    </div>
  );
};

// Single horizontal rule separating sub-sections inside the Identity
// container. Fades from a subtle tint to transparent for a soft editorial
// feel. Optional lock + trailing metadata (e.g. "7.5 avg").
const SectionDivider = ({ label, locked, lockLabel, trailing }) => (
  <div className="flex items-center gap-3 pt-1">
    <span
      className="text-xs font-normal lowercase tracking-normal whitespace-nowrap"
      style={{ color: "var(--color-text-secondary)" }}
    >
      {label}
    </span>
    {locked && (
      <Lock
        className="w-3 h-3 opacity-60 -ml-1.5"
        style={{ color: "var(--color-text-secondary)" }}
        aria-label={lockLabel}
      />
    )}
    <div
      aria-hidden="true"
      className="flex-1 h-px"
      style={{
        background:
          "linear-gradient(90deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.02) 100%)",
      }}
    />
    {trailing !== undefined && trailing !== null && (
      <span
        className="text-xs tabular-nums whitespace-nowrap"
        style={{ color: "var(--color-text-secondary)" }}
      >
        {trailing}
      </span>
    )}
  </div>
);

// Floating sticky anchor nav — pops in once the viewer scrolls past the
// hero so long profiles stay navigable without cluttering the initial
// fold.
const AnchorNav = ({ sections }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 320);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!sections || sections.length < 2) return null;
  const scrollToId = (id) => {
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-40 transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3 pointer-events-none"
      }`}
      data-testid="profile-anchor-nav"
    >
      <div
        className="flex items-center gap-0.5 rounded-full px-1.5 py-1"
        style={{
          background: "rgba(20, 15, 35, 0.78)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.28)",
        }}
      >
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => scrollToId(s.id)}
            className="text-[11px] font-medium px-3 py-1.5 rounded-full transition-colors hover:text-white"
            style={{ color: "var(--color-text-secondary)" }}
            data-testid={`anchor-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Focus-area bar uses the area's canonical colour straight from the
// payload — each Wheel of Life area carries its own hex (green for
// Health, indigo for Career, etc.). We render a 90% → 50% opacity
// gradient of that hue for a soft, intentional bar.
const focusBarGradient = (color) => {
  const c = color || "#7c3aed";
  return `linear-gradient(90deg, ${c} 0%, ${c}cc 100%)`;
};

// "Joined Mar 2024" formatter. Returns "" on bad input so callers can
// just inline the result.
const formatJoinedDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

// ── Main page ─────────────────────────────────────────────────
const PublicProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [gratOpen, setGratOpen] = useState(false);
  const [shareInsightOpen, setShareInsightOpen] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [exchange, setExchange] = useState(null);
  // Authed, in-circle viewers get the richer "offered" composer wired to
  // their existing connection row. Cache the row so we can feed it directly.
  const [myConnection, setMyConnection] = useState(null);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API}/public/profile/${encodeURIComponent(username)}`, {
        credentials: "include",
      });
      if (res.status === 404) { setNotFound(true); return null; }
      if (res.ok) { const d = await res.json(); setProfile(d); return d; }
    } catch { setNotFound(true); }
    finally { setLoading(false); }
    return null;
  };

  const fetchExchange = async () => {
    try {
      const res = await fetch(`${API}/public/profile/${encodeURIComponent(username)}/exchange`, {
        credentials: "include",
      });
      if (res.ok) setExchange(await res.json());
    } catch { /* non-critical */ }
  };

  useEffect(() => { fetchProfile(); /* eslint-disable-next-line */ }, [username]);

  // Load circle-only exchange data once connection is known.
  useEffect(() => {
    if (profile?.relationship?.state === "connected") {
      fetchExchange();
      // Also fetch the caller's connection row so we can open the authed
      // "offered" composer without asking for name/email/relationship.
      (async () => {
        try {
          const cid = profile.relationship?.connection_id;
          if (!cid) return;
          const res = await fetch(`${API}/network`, { credentials: "include" });
          if (!res.ok) return;
          const rows = await res.json();
          const match = (rows || []).find(r => r.connection_id === cid);
          if (match) setMyConnection(match);
        } catch { /* non-critical */ }
      })();
    }
    // eslint-disable-next-line
  }, [profile?.relationship?.state, username]);

  // Resume any intent (follow/connect) queued while visitor was anonymous.
  // Fires once the user becomes authenticated AND the profile payload is
  // available (so we know their current relationship/follow state).
  useEffect(() => {
    if (!user || !profile) return;
    const intent = consumePendingIntent(username);
    if (!intent) return;
    if (intent.action === "follow" && !profile.is_following) {
      toggleFollow(true);
    } else if (intent.action === "connect" && profile.relationship?.state === "none") {
      setRequestOpen(true);
    }
    // eslint-disable-next-line
  }, [user, profile?.username]);

  const toggleFollow = async (forceFollow = null) => {
    if (!user) {
      setPendingIntent({ action: "follow", username });
      googleSignIn({ returnUrl: `/${username}` });
      return;
    }
    if (followBusy) return;
    setFollowBusy(true);
    const shouldFollow = forceFollow !== null ? forceFollow : !profile.is_following;
    try {
      const res = await fetch(`${API}/follow/${encodeURIComponent(username)}`, {
        method: shouldFollow ? "POST" : "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Couldn't update follow state");
      const data = await res.json();
      setProfile((p) => p && { ...p, is_following: data.is_following, follower_count: data.follower_count });
      if (shouldFollow) toast.success(`Following ${firstName}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setFollowBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 via-white to-indigo-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-violet-50 via-white to-indigo-50 px-6 text-center">
        <SEO title="Profile not found" noIndex />
        <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mb-4">
          <UserPlus className="w-8 h-8 text-violet-400" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">{t("publicProfile.notFoundTitle")}</h1>
        <p className="text-slate-600 mb-6">
          @{username} {t("publicProfile.notFoundSuffix")}
        </p>
        <Button onClick={() => navigate("/")} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full">
          {t("publicProfile.startYourOwn")}
        </Button>
      </div>
    );
  }

  const firstName = (profile.name || "Someone").split(" ")[0];
  const isSelf = profile.relationship?.state === "self";
  const isConnected = profile.relationship?.state === "connected";
  const isPending = profile.relationship?.state === "pending";

  const handleConnect = () => {
    if (!user) {
      setPendingIntent({ action: "connect", username });
      googleSignIn({ returnUrl: `/${username}` });
      return;
    }
    setRequestOpen(true);
  };

  // Build per-user OG metadata so share previews (Slack, iMessage, WhatsApp,
  // Twitter/X, LinkedIn, Facebook) show the user's avatar + tagline instead
  // of the generic site defaults. Prefer absolute https URLs for og:image
  // — most crawlers reject protocol-relative or relative paths.
  const avatarUrl = profile.picture && /^https?:\/\//.test(profile.picture)
    ? profile.picture
    : null;
  const seoDesc = (profile.bio && profile.bio.trim())
    || `${firstName}'s profile on Evoluras · ${profile.circle_count ?? 0} in Circle · ${profile.wall_total ?? 0} gratitude notes`;

  return (
    <>
      <SEO
        title={`${firstName} (@${profile.username}) on Evoluras`}
        description={seoDesc}
        path={`/${profile.username}`}
        type="profile"
        ogImage={avatarUrl || undefined}
        ogImageWidth={avatarUrl ? 512 : undefined}
        ogImageHeight={avatarUrl ? 512 : undefined}
        twitterCard={avatarUrl ? "summary" : "summary_large_image"}
      />

      <div className="min-h-screen relative overflow-hidden"
        data-testid="public-profile-page">
        {/* Cover-as-page-backdrop — a heavily blurred + dimmed copy of the
            user's cover image fills the entire PAGE WRAPPER (not the full
            viewport) so it stops at the sidebar instead of covering it.
            A dark vertical scrim on top guarantees text remains legible
            regardless of the cover's exposure. Sits at z-0 so it visually
            overlays the body's solid app-mode background; all page content
            that follows is wrapped in `relative z-10`. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 z-0 pointer-events-none overflow-hidden"
          data-testid="profile-page-backdrop"
        >
          <img
            src={profile.cover_image || "/api/assets/default-cover.jpg"}
            alt=""
            className="absolute inset-0 w-full h-full object-cover scale-110"
            style={{ filter: "blur(60px) brightness(0.55) saturate(1.1)" }}
            loading="eager"
          />
          {/* Dark vertical scrim → guarantees contrast under text, especially
              for low-key covers. Stronger at the bottom where most copy sits. */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,5,25,0.45) 0%, rgba(10,5,25,0.7) 40%, rgba(10,5,25,0.92) 100%)",
            }}
          />
        </div>

        <div className="relative z-10">

        {/* Sticky anchor nav — appears after the viewer scrolls past the
            hero. Pops over everything else, centered top. */}
        <AnchorNav
          sections={[
            ...((profile.top_values || []).length > 0
              || (profile.top_strengths || []).length > 0
              || (profile.wheel_summary && (profile.wheel_summary.areas || []).length > 0)
              ? [{ id: "profile-identity", label: "Identity" }]
              : []),
            ...(isConnected ? [{ id: "circle-exchange", label: "Exchange" }] : []),
            ...(profile.can_view_wall && (profile.public_cards || []).length > 0
              ? [{ id: "profile-wall", label: "Wall" }]
              : []),
          ]}
        />
        {/* ── COVER BANNER (A1 contained card + A2 duotone + A3 ribbon) ── */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full overflow-hidden rounded-2xl shadow-sm"
            style={{
              aspectRatio: "1200 / 340",
              maxHeight: "320px",
              background: "linear-gradient(135deg, #1f1a3a 0%, #5d4481 50%, #e8b78a 100%)",
            }}
            data-testid="profile-cover-banner"
          >
            <img
              src={profile.cover_image || "/api/assets/default-cover.jpg"}
              alt={`${firstName}'s cover`}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            {/* Duotone violet scrim — ties any cover to the brand + helps contrast */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(135deg, rgba(83,74,183,0.32) 0%, rgba(26,26,46,0.10) 45%, rgba(26,26,46,0.55) 100%)",
              }}
            />
            {/* Bottom fade blends into the page */}
            <div
              className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 100%)",
              }}
            />

            {/* Floating circle badge — bottom-right, collapsible (single
                dot until hovered → expands into avatar stack) so the
                cover composition stays uncluttered. */}
            <CircleRibbon
              members={profile.circle_preview}
              count={profile.circle_count}
              firstName={firstName}
            />
          </motion.div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">

          {/* ── HERO (F1 asymmetric) ─────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="relative z-10 mb-10 flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-7"
          >
            {/* Duotone violet→indigo ring — quieter than the old conic
                gradient, single layer (no inner white box) so dark-mode
                avatars don't feel chalky. */}
            <div
              className="rounded-full p-[2px] w-28 h-28 sm:w-36 sm:h-36 flex-shrink-0 self-center sm:self-auto -mt-16 sm:-mt-20 ml-0 sm:ml-2"
              style={{
                background:
                  "linear-gradient(135deg, #a78bfa 0%, #7c3aed 50%, #4f46e5 100%)",
                boxShadow:
                  "0 0 28px rgba(124, 58, 237, 0.28), 0 14px 34px rgba(20, 10, 50, 0.32)",
              }}
              data-testid="profile-avatar-ring"
            >
              {profile.picture ? (
                <motion.img
                  src={profile.picture}
                  alt={profile.name}
                  className="w-full h-full rounded-full object-cover"
                  initial={{ scale: 0.92, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                />
              ) : (
                <div
                  className="w-full h-full rounded-full bg-violet-900/50 text-violet-100 text-4xl font-semibold flex items-center justify-center"
                  data-testid="profile-avatar-fallback"
                >
                  {firstName.charAt(0)}
                </div>
              )}
            </div>

            {/* Name / handle / bio / stats / CTAs — stacked to the right, below cover */}
            <div className="flex-1 min-w-0 text-center sm:text-left pt-1 sm:pt-3">
              <h1 className="text-3xl sm:text-4xl font-bold mb-0.5"
                style={{ color: "var(--color-text-primary)" }}
                data-testid="profile-name">
                {profile.name}
              </h1>
              <p className="text-sm font-medium mb-3"
                style={{ color: "#c4b5fd" }}
                data-testid="profile-handle">
                @{profile.username}
              </p>

              {/* Tagline / bio — one line, italic, muted */}
              {profile.bio && (
                <p className="text-sm italic leading-relaxed max-w-xl mb-4 mx-auto sm:mx-0"
                  style={{ color: "var(--color-text-secondary)" }}
                  data-testid="profile-bio">
                  {profile.bio}
                </p>
              )}

              {/* Inline stats strip — no pill, no dividers, just editorial text */}
              <p
                className="text-xs mb-5 flex items-center gap-1.5 flex-wrap justify-center sm:justify-start"
                style={{ color: "var(--color-text-secondary)" }}
                data-testid="profile-counts"
              >
                <span data-testid="profile-follower-count">
                  <span className="font-semibold" style={{ color: "var(--color-text-primary)" }}>
                    {profile.follower_count ?? 0}
                  </span>{" "}
                  follower{(profile.follower_count ?? 0) === 1 ? "" : "s"}
                </span>
                <span aria-hidden="true" className="opacity-50">·</span>
                <span data-testid="profile-circle-count">
                  <span className="font-semibold" style={{ color: "var(--color-text-primary)" }}>
                    {profile.circle_count ?? (profile.circle_preview || []).length}
                  </span>{" "}
                  in Circle
                </span>
                <span aria-hidden="true" className="opacity-50">·</span>
                <span data-testid="profile-following-count">
                  <span className="font-semibold" style={{ color: "var(--color-text-primary)" }}>
                    {profile.following_count ?? 0}
                  </span>{" "}
                  following
                </span>
                {formatJoinedDate(profile.joined_at) && (
                  <>
                    <span aria-hidden="true" className="opacity-50">·</span>
                    <span data-testid="profile-joined">
                      Joined {formatJoinedDate(profile.joined_at)}
                    </span>
                  </>
                )}
              </p>

              {isSelf && (
                <p className="text-xs italic mb-4"
                  style={{ color: "var(--color-text-secondary)" }}>
                  This is how your profile looks to others.
                </p>
              )}

              {/* CTA cluster — one primary pill + icon-only secondary circles */}
              {!isSelf && (
                <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                  {isConnected ? (
                    <Button disabled
                      className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-full px-6 py-5 text-sm font-semibold cursor-default hover:bg-emerald-500/15"
                      data-testid="profile-cta-connected">
                      <Check className="w-4 h-4 mr-1.5" />
                      {t("publicProfile.inCircle")}
                    </Button>
                  ) : isPending ? (
                    <Button disabled
                      className="bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-full px-6 py-5 text-sm font-semibold cursor-default hover:bg-amber-500/15"
                      data-testid="profile-cta-pending">
                      <Clock className="w-4 h-4 mr-1.5" />
                      {t("publicProfile.requestSent")}
                    </Button>
                  ) : (
                    <Button onClick={handleConnect}
                      className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-7 py-5 text-sm font-semibold shadow-lg shadow-violet-600/30"
                      data-testid="profile-cta-connect">
                      <UserPlus className="w-4 h-4 mr-1.5" />
                      {t("publicProfile.requestJoin", { name: firstName })}
                    </Button>
                  )}

                  <SecondaryPill
                    onClick={() => toggleFollow()}
                    disabled={followBusy}
                    active={profile.is_following}
                    label={profile.is_following ? t("publicProfile.following") : t("publicProfile.follow")}
                    icon={profile.is_following
                      ? <UserCheck className="w-3.5 h-3.5" />
                      : <UserPlus className="w-3.5 h-3.5" />}
                    testId={profile.is_following ? "profile-cta-unfollow" : "profile-cta-follow"}
                  />

                  {/* Send gratitude — available to public visitors when wall is public,
                      or to Circle members when the per-tool gratitude preference is on. */}
                  {((profile.wall_is_public && !isConnected) || (isConnected && profile.can_give_proactive_insight?.gratitude)) && (
                    <SecondaryPill
                      onClick={() => setGratOpen(true)}
                      label={t("publicProfile.sendGratitude")}
                      icon={<HeartHandshake className="w-3.5 h-3.5" />}
                      testId="profile-cta-send-gratitude"
                    />
                  )}

                  {/* Share proactive insight — Circle-only, gated by owner's SWOT toggle */}
                  {profile.can_give_proactive_insight?.swot && (
                    <SecondaryPill
                      onClick={() => setShareInsightOpen(true)}
                      label={t("publicProfile.shareInsight", { name: firstName })}
                      icon={<Sparkles className="w-3.5 h-3.5" />}
                      testId="profile-cta-share-insight"
                    />
                  )}

                  {/* Share profile (web share API) — copies the /api/share/{handle}
                      URL which renders rich OG previews for every crawler
                      (Slack, WhatsApp, iMessage, FB, LinkedIn) and auto-
                      redirects human clickers back to the SPA page. */}
                  <SecondaryPill
                    onClick={() => {
                      const origin = typeof window !== "undefined" ? window.location.origin : "";
                      const shareUrl = `${origin}/api/share/${encodeURIComponent(profile.username)}`;
                      if (navigator.share) {
                        navigator.share({
                          title: `${profile.name} on Evoluras`,
                          text: `${firstName}'s profile`,
                          url: shareUrl,
                        }).catch(() => { /* user cancel */ });
                      } else if (navigator.clipboard && shareUrl) {
                        navigator.clipboard.writeText(shareUrl)
                          .then(() => toast.success("Link copied"))
                          .catch(() => toast.error("Couldn't copy link"));
                      }
                    }}
                    label="Share"
                    icon={<Share2 className="w-3.5 h-3.5" />}
                    testId="profile-cta-share"
                  />
                </div>
              )}
            </div>
          </motion.div>

          {/* ── CIRCLE EXCHANGE (visible only when connected) ───── */}
          {isConnected && (
            <div data-testid="circle-exchange">
              <CircleExchangeSection exchange={exchange} firstName={firstName} />
            </div>
          )}

          {/* ── Identity block ───────────────────────────────────────
              Unified container grouping Values, Strengths, and Focus
              areas. Hero is full-width; below it a 2-col composition
              (lg+) puts Values + Focus on the left and Strengths on
              the right. Wheel-of-Life canonical colours flow through
              the whole card — bar gradients, strength accents, and a
              tinted hero medallion. */}
          {(
            (profile.top_values || []).length > 0 ||
            (profile.top_strengths || []).length > 0 ||
            (profile.wheel_summary && (profile.wheel_summary.areas || []).length > 0)
          ) && (
            <motion.section
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.16, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="mb-10 rounded-2xl p-5 sm:p-7"
              style={{
                background: "rgba(255,255,255,0.025)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
              data-testid="profile-identity"
            >
              {(() => {
                const values = profile.top_values || [];
                const [hero, ...restValues] = values;
                const strengths = profile.top_strengths || [];
                const wheel = profile.wheel_summary;
                const heroTint = profile.top_focus_color || "#7c3aed";
                const hasLeft = restValues.length > 0 || (wheel && (wheel.areas || []).length > 0);
                const hasRight = strengths.length > 0;
                return (
                  <>
                    {/* ── Hero — full width ─────────────────────── */}
                    {hero && (
                      <div
                        className="flex items-center gap-5 sm:gap-6 mb-7"
                        title={hero.description || undefined}
                        data-testid="profile-top-values"
                      >
                        {/* Tinted medallion behind the value icon — colour
                            comes from the user's #1 focus area so the
                            hero subtly signals their life centre. */}
                        <div
                          className="w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{
                            background: `radial-gradient(circle at 30% 30%, ${heroTint}38 0%, ${heroTint}12 60%, transparent 100%)`,
                            boxShadow: `0 0 32px ${heroTint}25`,
                          }}
                        >
                          <ValueIcon
                            id={hero.id}
                            name={hero.name}
                            size={64}
                            testId="top-value-icon-1"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-[10px] font-medium uppercase tracking-[0.18em] mb-1"
                            style={{ color: "var(--color-text-secondary)" }}
                          >
                            {firstName}'s top value
                          </p>
                          <h2
                            className="text-2xl sm:text-3xl font-semibold mb-1.5"
                            style={{ color: "var(--color-text-primary)" }}
                            data-testid="top-value-1"
                          >
                            {hero.name}
                          </h2>
                          {hero.description && (
                            <p
                              className="text-sm italic leading-relaxed max-w-2xl"
                              style={{ color: "var(--color-text-secondary)" }}
                            >
                              "{hero.description}"
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ── Body — 2-col on lg, stacked on mobile ── */}
                    <div className={`grid gap-7 ${hasLeft && hasRight ? "lg:grid-cols-2 lg:gap-10" : "grid-cols-1"}`}>
                      {/* LEFT — also values + focus areas */}
                      {hasLeft && (
                        <div className="space-y-7">
                          {restValues.length > 0 && (
                            <div>
                              <SectionDivider
                                label="values"
                                locked={
                                  profile.relationship?.state === "self" &&
                                  profile.core_values_visibility &&
                                  profile.core_values_visibility !== "public"
                                }
                                lockLabel={
                                  profile.core_values_visibility === "private"
                                    ? "Only you"
                                    : "Inner Circle only"
                                }
                              />
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mt-3">
                                {restValues.map((v, idx) => (
                                  <div
                                    key={v.value_id || idx}
                                    className="flex items-center gap-3"
                                    title={v.description || undefined}
                                    data-testid={`top-value-${idx + 2}`}
                                  >
                                    <ValueIcon
                                      id={v.id}
                                      name={v.name}
                                      size={36}
                                      testId={`top-value-icon-${idx + 2}`}
                                    />
                                    <div className="min-w-0">
                                      <p
                                        className="text-sm font-semibold truncate"
                                        style={{ color: "var(--color-text-primary)" }}
                                      >
                                        {v.name}
                                      </p>
                                      <p
                                        className="text-[11px]"
                                        style={{ color: "var(--color-text-secondary)" }}
                                      >
                                        Core value #{v.priority || idx + 2}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {wheel && (wheel.areas || []).length > 0 && (
                            <div>
                              <SectionDivider
                                label={wheel.is_focus_only ? "focus areas" : "wheel of life"}
                                locked={
                                  profile.relationship?.state === "self" &&
                                  profile.wheel_of_life_visibility &&
                                  profile.wheel_of_life_visibility !== "public"
                                }
                                lockLabel={
                                  profile.wheel_of_life_visibility === "private"
                                    ? "Only you"
                                    : "Inner Circle only"
                                }
                                trailing={`${wheel.average} avg`}
                              />
                              <div
                                className="space-y-2.5 mt-3"
                                data-testid="profile-wheel-summary"
                              >
                                {wheel.areas.map((a, idx) => {
                                  const score = a.score || 0;
                                  const pct = Math.max(0, Math.min(100, score * 10));
                                  const c = a.color || "#7c3aed";
                                  return (
                                    <div
                                      key={a.key || idx}
                                      className="flex items-center gap-2.5"
                                      data-testid={`wheel-area-${a.key || idx}`}
                                    >
                                      <span
                                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                        style={{ background: c, boxShadow: `0 0 6px ${c}80` }}
                                        aria-hidden="true"
                                      />
                                      <span
                                        className="text-xs w-28 truncate"
                                        style={{ color: "var(--color-text-primary)" }}
                                      >
                                        {a.label}
                                      </span>
                                      <div
                                        className="flex-1 h-1.5 rounded-full overflow-hidden"
                                        style={{ background: "rgba(255,255,255,0.06)" }}
                                      >
                                        <div
                                          className="h-full rounded-full transition-all"
                                          style={{
                                            width: `${pct}%`,
                                            background: focusBarGradient(c),
                                          }}
                                        />
                                      </div>
                                      <span
                                        className="text-xs tabular-nums w-5 text-right"
                                        style={{ color: "var(--color-text-secondary)" }}
                                      >
                                        {score}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                              {wheel.average !== undefined && (
                                <span className="sr-only" data-testid="wheel-avg">
                                  {wheel.average} avg
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* RIGHT — strengths */}
                      {hasRight && (
                        <div
                          className={hasLeft ? "lg:pl-8 lg:border-l lg:border-white/[0.06]" : ""}
                        >
                          <SectionDivider
                            label="strengths"
                            locked={
                              profile.relationship?.state === "self" &&
                              profile.swot_visibility &&
                              profile.swot_visibility !== "public"
                            }
                            lockLabel={
                              profile.swot_visibility === "private"
                                ? "Only you"
                                : "Inner Circle only"
                            }
                          />
                          <ul
                            className="space-y-3 mt-3"
                            data-testid="profile-top-strengths"
                          >
                            {strengths.map((s, idx) => {
                              const accent = s.linked_color || profile.top_focus_color || "#a78bfa";
                              return (
                                <li
                                  key={s.item_id || idx}
                                  className="pl-3.5 relative"
                                  data-testid={`top-strength-${idx + 1}`}
                                >
                                  <span
                                    aria-hidden="true"
                                    className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full"
                                    style={{
                                      background: accent,
                                      boxShadow: `0 0 8px ${accent}55`,
                                    }}
                                  />
                                  <p
                                    className="text-sm leading-relaxed"
                                    style={{ color: "var(--color-text-primary)" }}
                                  >
                                    {s.text}
                                  </p>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </motion.section>
          )}

          {/* ── Wall preview ──────────────────────────────────────────
              Shown to anyone the backend decided can see the wall — that is
              public viewers (when wall is public) AND circle members (even
              if wall is private — they see public + circle cards). Label
              adapts so circle members know it's a subset. */}
          {profile.can_view_wall && profile.public_cards.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.20, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="mb-10"
              data-testid="profile-wall"
            >
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Heart className="w-3.5 h-3.5 text-violet-600 fill-violet-600" />
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                      {profile.wall_is_public
                        ? "From their gratitude wall"
                        : `From ${firstName}'s circle wall`}
                    </p>
                  </div>
                  <h2 className="text-lg font-bold text-slate-900">
                    {profile.wall_total} note{profile.wall_total !== 1 ? "s" : ""}
                  </h2>
                </div>
                <a href={profile.username ? `/${profile.username}/gratitude` : `/u/${profile.slug}/gratitude`}
                  className="text-xs font-medium text-violet-600 hover:text-violet-800 hover:underline flex items-center gap-1"
                  data-testid="profile-view-wall">
                  View full wall <ArrowRight className="w-3 h-3" />
                </a>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
                {profile.public_cards.map((c) => (
                  <GratitudeCard
                    key={c.card_id}
                    card={{
                      ...c,
                      perspective: c.type === "self" ? "mine" : "received",
                      visibility: "public",
                    }}
                    readOnly
                  />
                ))}
              </div>
            </motion.div>
          )}

          {/* Locked wall placeholder — quiet italic line, no bordered box */}
          {!profile.can_view_wall && (
            <p
              className="text-center text-xs italic mt-6 mb-10 flex items-center justify-center gap-1.5"
              style={{ color: "var(--color-text-placeholder)" }}
              data-testid="profile-wall-locked"
            >
              <Lock className="w-3 h-3" />
              Gratitude notes are kept close — only {firstName}'s Circle can see them.
            </p>
          )}

          {/* ── Footer ───────────────────────────────────────────────
              Only shown to anonymous visitors — the whole point of this
              footer is to convert them. Authed users (whether looking at
              their own profile or someone else's) don't need the pitch. */}
          {!user && (
            <div className="text-center mt-10 pt-6 border-t border-violet-100">
              <p className="text-xs text-slate-500 mb-2">Want a profile like this?</p>
              <Button variant="ghost" onClick={() => navigate("/")}
                className="text-violet-700 hover:text-violet-800 hover:bg-violet-50 rounded-full text-xs font-medium"
                data-testid="profile-build-own">
                Build yours on Evoluras <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Connection request dialog */}
      <RequestCircleDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        ownerFirstName={firstName}
        username={profile.username}
        onSuccess={() => fetchProfile()}
      />

      {/* Send-gratitude dialog — authed Circle viewers use the rich
          "offered" composer pre-wired to the connection (no name/email/
          relationship re-ask); everyone else falls back to the public
          visitor composer. */}
      {isConnected && user ? (
        <AddGratitudeDialog
          open={gratOpen}
          onClose={() => setGratOpen(false)}
          mode="offered"
          connections={myConnection ? [myConnection] : []}
          initialConnectionId={profile.relationship?.connection_id}
          onCreated={fetchProfile}
        />
      ) : (
        <PublicAddGratitudeDialog
          open={gratOpen}
          onOpenChange={setGratOpen}
          ownerName={profile.name}
          ownerFirstName={firstName}
          slug={profile.slug}
          onSuccess={fetchProfile}
        />
      )}

      {/* Share an unsolicited insight with the profile owner. Only mounts
          when can_give_proactive_insight is true (Circle member + owner's
          toggle ON). Posts to /api/insights/proactive with a single SWOT
          item — keeps the dialog tiny and focused. */}
      <ShareInsightDialog
        open={shareInsightOpen}
        onOpenChange={setShareInsightOpen}
        ownerName={profile.name}
        ownerFirstName={firstName}
        connectionId={profile.relationship?.connection_id}
      />
    </>
  );
};

export default PublicProfilePage;
